import * as React from 'react';
import matchSorter from 'match-sorter';
import classify from 'src/utils/classify';

import Loading from 'src/components/lib/loading/index';

import css from './emoji-picker.css';

// TODO (kyle): we should remove the async loadinging of the lib and use
// React.lazy on this component.

interface EmojiLibInterface {
  lib: {
    [name: string]: EmojiLibObject;
  };
  ordered: string[];
}

type EmojiLibObject = {
  keywords: string[];
  char: string;
  fitzpatrick_scale: boolean;
  category: string;
};
type EmojiObject = EmojiLibObject & {
  name: string;
};
type EmojiCategories = Map<string, EmojiObject[]>;

type ClassNames = Readonly<{
  root?: string;
  emojiButton?: string;
}>;

export type Props = {
  classNames: ClassNames;
  onClose: () => unknown;
  onSelect: (arg1: string) => unknown;
  t: (key: string, fallback: string) => string;
  makeKey: (key: string) => string;
};

type State = {
  isLoaded: boolean;
  categories: EmojiCategories;
  list: EmojiObject[];
  searchValue: string;
};

export default class EmojiPicker extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    classNames: {},
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, State, any>'.
  state: State = {
    isLoaded: false,
    categories: new Map(),
    list: [],
    searchValue: '',
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, State, any>'.
  componentDidMount() {
    // NOTE (kyle): we defer loading the emojilib here because successive
    // calls to `import()` will be synchronous and delay the next paint frame.
    setTimeout(() => {
      // @ts-expect-error - TS7016 - Could not find a declaration file for module 'emojilib'. '/Users/diwaker/SpacedOut/ui-server/node_modules/emojilib/index.js' implicitly has an 'any' type.
      import('emojilib').then(this.loadEmojiFromLib.bind(this));
    }, 0);
  }

  loadEmojiFromLib({lib, ordered}: EmojiLibInterface) {
    // Push ordered emojis into their respective category keys in the Map
    const categories: EmojiCategories = new Map();
    const list: Array<EmojiObject> = [];

    ordered.forEach((emojiName: string) => {
      const libEmoji: EmojiLibObject = lib[emojiName];
      const {category} = libEmoji;
      const emoji: EmojiObject = {
        name: emojiName,
        ...libEmoji,
      };

      let group = categories.get(category);
      if (!group) {
        group = [];
        categories.set(category, group);
      }

      group.push(emoji);
      list.push(emoji);
    });

    // Trigger component draw on load complete
    this.setState({categories, list, isLoaded: true});
  }

  handleSearchChange: (arg1: React.ChangeEvent<HTMLInputElement>) => void = (
    evt,
  ) => {
    this.setState({
      searchValue: evt.target.value,
    });
  };

  handleSearchKeyDown: (arg1: React.KeyboardEvent<HTMLInputElement>) => void = (
    evt,
  ) => {
    if (evt.key === 'Escape') {
      this.props.onClose();
    }
  };

  getSearchMatches(): EmojiObject[] {
    const {searchValue, list} = this.state;
    return matchSorter(list, searchValue, {keys: ['name', 'keywords']});
  }

  handleEmojiSelect: (arg1: EmojiObject) => void = (emoji) => {
    this.props.onSelect(emoji.char);
    this.props.onClose();
  };

  renderResults(): React.ReactNode {
    const {isLoaded, searchValue, categories} = this.state;
    const isSearching = searchValue !== '';

    if (!isLoaded) {
      return <Loading />;
    }

    return isSearching ? (
      <SearchResults
        classNames={this.props.classNames}
        list={this.getSearchMatches()}
        onSelect={this.handleEmojiSelect}
        t={this.props.t}
      />
    ) : (
      <Categories
        classNames={this.props.classNames}
        categories={categories}
        onSelect={this.handleEmojiSelect}
        t={this.props.t}
        makeKey={this.props.makeKey}
      />
    );
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, State, any>'.
  render(): React.ReactNode {
    return (
      <div className={this.props.classNames.root}>
        <input
          type="search"
          placeholder={`${this.props.t('Search_emojis', 'Search emojis')}...`}
          className={css.searchInput}
          value={this.state.searchValue}
          onChange={this.handleSearchChange}
          onKeyDown={this.handleSearchKeyDown}
          // @ts-expect-error - TS2322 - Type '(el: HTMLInputElement | null) => void | null' is not assignable to type 'Ref<HTMLInputElement> | undefined'.
          ref={(el) => el && el.focus()}
        />
        {this.renderResults()}
      </div>
    );
  }
}

const EmojiButton = ({
  emoji,
  onSelect,
  className,
}: {
  emoji: EmojiObject;
  onSelect: (emoji: EmojiObject) => void;
  className?: string;
}) => (
  <button
    className={classify(css.emojiButton, className)}
    title={emoji.name}
    onClick={() => onSelect(emoji)}
  >
    {emoji.char}
  </button>
);

// @ts-expect-error - TS7031 - Binding element 'categories' implicitly has an 'any' type. | TS7031 - Binding element 'onSelect' implicitly has an 'any' type. | TS7031 - Binding element 'classNames' implicitly has an 'any' type. | TS7031 - Binding element 't' implicitly has an 'any' type. | TS7031 - Binding element 'makeKey' implicitly has an 'any' type.
function Categories({categories, onSelect, classNames, t, makeKey}) {
  const categoriesArray = Array.from(categories);

  return (
    <div className={css.emojiContainer}>
      {/* @ts-expect-error - TS2345 - Argument of type '([category, emojis]: [any, any]) => React.JSX.Element' is not assignable to parameter of type '(value: unknown, index: number, array: unknown[]) => Element'. */}
      {categoriesArray.map(([category, emojis]: [any, any]) => {
        const categoryName = category.replace(/_/g, ' ');
        return (
          <section key={category} className={css.emojiCategory}>
            <h6>{t(makeKey(category), categoryName)}</h6>
            <div className={css.emojiGroup}>
              {/* @ts-expect-error - TS7006 - Parameter 'emoji' implicitly has an 'any' type. */}
              {emojis.map((emoji) => (
                <EmojiButton
                  className={classNames.emojiButton}
                  key={emoji.name}
                  emoji={emoji}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function SearchResults({
  list,
  onSelect,
  classNames,
  t,
}: {
  list: EmojiObject[];
  onSelect: (emoji: EmojiObject) => void;
  classNames: ClassNames;
  t: (key: string, fallback: string) => string;
}) {
  if (list.length === 0) {
    return (
      <div className={css.emojiContainer}>
        <div className={css.emojiGroup}>
          <span className={css.emojiEmptyState}>
            {t('No_matching_emojis', 'No matching emojis')}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className={css.emojiContainer}>
      <div className={css.emojiGroup}>
        {list.map((emoji) => (
          <EmojiButton
            className={classNames.emojiButton}
            key={emoji.name}
            emoji={emoji}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}
